/**
 * Realtime Context
 * React context for managing realtime data synchronization across the app
 */

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { usePoll } from '../hooks/usePoll';
import { RealtimeService } from '../services/RealtimeService';
import { toast } from 'sonner';

interface RealtimeContextValue {
  // State
  isConnected: boolean;
  pendingUpdatesCount: number;
  isEditing: boolean;
  isFormOpen: boolean;
  
  // Controls
  setEditing: (editing: boolean) => void;
  setFormOpen: (open: boolean) => void;
  processPendingUpdates: () => void;
  
  // Configuration
  showUpdateNotifications: boolean;
  setShowUpdateNotifications: (show: boolean) => void;
}

const RealtimeContext = createContext<RealtimeContextValue | undefined>(undefined);

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const [isConnected, setIsConnected] = useState(true);
  const [pendingUpdatesCount, setPendingUpdatesCount] = useState(0);
  const [isEditing, setIsEditingState] = useState(false);
  const [isFormOpen, setIsFormOpenState] = useState(false);
  const [showUpdateNotifications, setShowUpdateNotifications] = useState(false);

  // Update pending count every second — paused automatically when tab hidden/offline.
  usePoll(() => setPendingUpdatesCount(RealtimeService.getPendingUpdateCount()), 1000);

  // Listen to global realtime events
  useEffect(() => {
    const unsubscribe = RealtimeService.addGlobalListener((update) => {
      // Handle visibility refresh
      if (update.type === 'visibility_refresh') {
        return;
      }

      // Handle pending updates notification
      if (update.type === 'pending_updates') {
        if (showUpdateNotifications) {
          toast.info('New data available', {
            description: 'Click to refresh and see the latest updates',
            action: {
              label: 'Refresh',
              onClick: () => {
                window.location.reload();
              },
            },
          });
        }
        return;
      }

      // Handle individual table updates
      if (update.table && update.eventType) {
        // console.log(`📬 Realtime Context: ${update.eventType} on ${update.table}`);
        
        if (showUpdateNotifications) {
          const messages = {
            INSERT: `New ${update.table.replace('_', ' ')} created`,
            UPDATE: `${update.table.replace('_', ' ')} updated`,
            DELETE: `${update.table.replace('_', ' ')} deleted`,
          };
          
          toast.success(messages[update.eventType as keyof typeof messages] || 'Data updated');
        }
      }
    });

    return unsubscribe;
  }, [showUpdateNotifications]);

  // Sync editing state with service
  const setEditing = useCallback((editing: boolean) => {
    setIsEditingState(editing);
    RealtimeService.setEditing(editing);
  }, []);

  // Sync form open state with service
  const setFormOpen = useCallback((open: boolean) => {
    setIsFormOpenState(open);
    RealtimeService.setFormOpen(open);
  }, []);

  // Process pending updates
  const processPendingUpdates = useCallback(() => {
    RealtimeService.processPendingUpdates();
    setPendingUpdatesCount(0);
  }, []);

  const value: RealtimeContextValue = {
    isConnected,
    pendingUpdatesCount,
    isEditing,
    isFormOpen,
    setEditing,
    setFormOpen,
    processPendingUpdates,
    showUpdateNotifications,
    setShowUpdateNotifications,
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime() {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
}